/**
 * Named world-population routines for the shot set (U19).
 *
 * A shot is a *named deterministic state*, and most interesting states are
 * reached far faster by placing entities directly than by replaying a command
 * log to minute twelve. Determinism still holds: these run before the seek and
 * use only the simulation's own spawn path.
 */

import type { Sim } from '../sim/index.js'
import { Vision } from '../sim/vision.js'
import { ID, defsForFaction, FACTION } from '../sim/data/entities.js'
import { CELL, TERRAIN } from '../sim/world.js'
import { Build } from '../sim/build.js'
import { Pathfind } from '../sim/pathfind.js'
import { ST } from '../sim/entities.js'

export type Populator = (sim: Sim) => void

const HELIOS_ROSTER = [
  ID.CommandYard,
  ID.SolarArray,
  ID.RegolithRefinery,
  ID.HabitatBlock,
  ID.Foundry,
  ID.CommsMast,
  ID.GuardianTurret,
  ID.OrbitalMirrorLance,
  ID.LandingField,
  ID.Mcv,
  ID.RegolithHarvester,
  ID.RifleSquad,
  ID.Engineer,
  ID.ScoutBuggy,
  ID.GuardianTank,
  ID.HornetGunship,
  ID.Marksman,
  ID.Outrider,
  ID.PrismWorks,
  ID.PrismTank,
  ID.SiegeBattery,
  ID.ResonanceVault,
  ID.Warden,
]

const ASCENDANCY_ROSTER = [
  ID.CoreNexus,
  ID.FusionCore,
  ID.NaniteRefinery,
  ID.ReplicationVat,
  ID.AssemblyHive,
  ID.SignalSpire,
  ID.ArcPylon,
  ID.LogicBomb,
  ID.LaunchSpire,
  ID.MobileNexusFrame,
  ID.NaniteHarvester,
  ID.DroneSwarm,
  ID.Infiltrator,
  ID.Skirmisher,
  ID.SiegeWalker,
  ID.WraithSkimmer,
  ID.Nullifier,
  ID.Vector,
  ID.ArcLancer,
  ID.VoidMortar,
  ID.Colossus,
]

/**
 * A contact sheet: one of everything, evenly spaced, on flat ground.
 *
 * The ground has to be flattened first. The first version of this spawned onto
 * whatever the map generator had put there, which was mostly a lake, and two
 * thirds of the roster silently failed to spawn.
 */
/**
 * Every unit and structure a faction builds, laid out on flat ground.
 *
 * **The roster must be complete.** It was not: the tier-three entries were missing
 * and the last row of a five-column layout fell outside the camera, so seven recipes
 * had no frame anywhere in the project that showed them. Anything absent here cannot
 * be previewed while it is designed, cannot be compared against its neighbours, and
 * cannot be checked after a change — a model sheet that omits a third of the roster
 * is worse than none, because it looks complete.
 *
 * Six columns rather than five, so a fuller roster does not push rows off the bottom.
 */
function sheet(sim: Sim, kinds: number[], owner: number): void {
  flatten(sim, 64, 64, 26)
  const columns = 6
  const spacing = 4
  const originX = 64 - ((columns - 1) * spacing) / 2
  const originY = 64 - ((Math.ceil(kinds.length / columns) - 1) * spacing) / 2
  kinds.forEach((kind, i) => {
    sim.spawnDef(
      kind,
      owner,
      Math.round(originX + (i % columns) * spacing),
      Math.round(originY + Math.floor(i / columns) * spacing),
    )
  })
}

/** Level and clear a square of map, so a model sheet is not half underwater. */
function flatten(sim: Sim, cx: number, cy: number, radius: number): void {
  const w = sim.world
  for (let y = cy - radius; y <= cy + radius; y++) {
    for (let x = cx - radius; x <= cx + radius; x++) {
      if (!w.inBounds(x, y)) continue
      const i = y * w.size + x
      w.flags[i] = CELL.Passable
      w.height[i] = 0
      w.terrain[i] = TERRAIN.Dust
      w.regolith[i] = 0
      w.ground[i] = 0
      w.scenery[i] = 0
    }
  }
  sim.registry.emit('world:passability', { cx, cy })
}

/** Lay a roster out on a grid, spaced so nothing overlaps. */
function grid(sim: Sim, kinds: number[], owner: number, ox: number, oy: number): void {
  kinds.forEach((kind, i) => {
    sim.spawnDef(kind, owner, ox + (i % 5) * 5, oy + Math.floor(i / 5) * 5)
  })
}

export const POPULATORS: Record<string, Populator> = {
  'early-base': (sim) => {
    sim.spawnDef(ID.CommandYard, 0, 17, 17)
    sim.spawnDef(ID.SolarArray, 0, 21, 17)
    sim.spawnDef(ID.RegolithRefinery, 0, 17, 21)
    sim.spawnDef(ID.RegolithHarvester, 0, 21, 22)
    sim.spawnDef(ID.GuardianTank, 0, 15, 21)
    reveal(sim)
  },

  /**
   * Every entity of one faction on a tight grid, for looking at the models.
   *
   * The roster shots spread the same units across the map at playing distance,
   * which is the right test for "can I tell them apart in a battle" and useless
   * for "does this building look like anything". Four cells apart at gameplay
   * zoom puts the whole faction on one screen at roughly fifty pixels a cell,
   * which is the size a person actually judges a model at.
   */
  'sheet-helios': (sim) => {
    sheet(sim, HELIOS_ROSTER, 0)
    reveal(sim)
  },

  'sheet-ascendancy': (sim) => {
    sheet(sim, ASCENDANCY_ROSTER, 1)
    reveal(sim)
  },

  'walker-march': (sim) => {
    flatten(sim, 64, 64, 14)
    const path = sim.registry.get<Pathfind>('pathfind')
    for (const [kind, x] of [[ID.SiegeWalker, 60], [ID.Colossus, 66]]) {
      const id = sim.spawnDef(kind, 1, x, 60)
      path.orderGroup([id], 72 * sim.world.size + x, ST.Moving)
    }
    reveal(sim)
  },

  'air-strike': (sim) => {
    // A flight over a ground force it is about to attack, with its landing
    // field behind it: the whole sortie loop in one frame.
    sim.spawnDef(ID.LandingField, 0, 58, 66)
    for (let i = 0; i < 4; i++) sim.spawnDef(ID.HornetGunship, 0, 62 + i, 62)
    for (let i = 0; i < 8; i++) {
      sim.spawnDef(i % 3 === 0 ? ID.DroneSwarm : ID.SiegeWalker, 1, 66 + (i % 4), 59 + ((i / 4) | 0))
    }
    sim.spawnDef(ID.LaunchSpire, 1, 70, 62)
    sim.spawnDef(ID.WraithSkimmer, 1, 68, 64)
    reveal(sim)
  },

  battle: (sim) => {
    for (let i = 0; i < 18; i++) {
      sim.spawnDef(i % 3 === 0 ? ID.RifleSquad : ID.GuardianTank, 0, 58 + (i % 6), 60 + ((i / 6) | 0))
    }
    for (let i = 0; i < 18; i++) {
      sim.spawnDef(i % 3 === 0 ? ID.DroneSwarm : ID.SiegeWalker, 1, 66 + (i % 6), 60 + ((i / 6) | 0))
    }
    sim.spawnDef(ID.GuardianTurret, 0, 56, 64)
    sim.spawnDef(ID.ArcPylon, 1, 72, 64)
    reveal(sim)
  },

  fog: (sim) => {
    // A scout with a small vision radius, so the explored / visible / unexplored
    // boundary is visible in one frame.
    sim.spawnDef(ID.ScoutBuggy, 0, 40, 40)
    sim.spawnDef(ID.CommandYard, 0, 38, 38)
    sim.spawnDef(ID.SiegeWalker, 1, 52, 40)
  },

  /**
   * A prism cluster mid-volley, so the beams and the links between the tanks
   * can be looked at. Three tanks close enough to feed each other, and enough
   * targets that they are all firing on the tick the shot is captured.
   */
  prism: (sim) => {
    flatten(sim, 64, 64, 14)
    for (let i = 0; i < 3; i++) sim.spawnDef(ID.PrismTank, 0, 60 + i, 64 + (i % 2))
    for (let i = 0; i < 5; i++) sim.spawnDef(ID.SiegeWalker, 1, 66 + (i % 3), 62 + i)
    reveal(sim)
  },

  /** Two squads side by side, one dug in, so the sandbags can be looked at. */
  digin: (sim) => {
    flatten(sim, 64, 64, 10)
    const build = sim.registry.get<Build>('build')
    for (let i = 0; i < 3; i++) {
      sim.spawnDef(ID.RifleSquad, 0, 62, 62 + i * 2)
      const dug = sim.spawnDef(ID.RifleSquad, 0, 66, 62 + i * 2)
      build.tryDeploy(0, dug)
    }
    // Marksmen alongside, since the two are meant to be told apart at a
    // glance: a squad is three figures, a marksman is one with a long barrel.
    for (let i = 0; i < 2; i++) {
      sim.spawnDef(ID.Marksman, 0, 59, 63 + i * 2)
      build.tryDeploy(0, sim.spawnDef(ID.Marksman, 0, 69, 63 + i * 2))
    }
    reveal(sim)
  },

  /**
   * Four IFVs, one per ring-mount fitting: empty, rifles, marksman, engineer.
   * The unit's whole idea is that a glance answers "what is inside", so the
   * four silhouettes have to be distinguishable side by side.
   */
  ifv: (sim) => {
    flatten(sim, 64, 64, 14)
    const build = sim.registry.get<Build>('build')
    sim.spawnDef(ID.Outrider, 0, 60, 59)
    const withRifles = sim.spawnDef(ID.Outrider, 0, 63, 59)
    build.tryLoad(0, sim.spawnDef(ID.RifleSquad, 0, 63, 58), withRifles)
    const withMarksman = sim.spawnDef(ID.Outrider, 0, 66, 59)
    build.tryLoad(0, sim.spawnDef(ID.Marksman, 0, 66, 58), withMarksman)
    const withEngineer = sim.spawnDef(ID.Outrider, 0, 69, 59)
    build.tryLoad(0, sim.spawnDef(ID.Engineer, 0, 69, 58), withEngineer)
    // The Ascendancy chassis below, loaded the same way, since both factions
    // share the silhouette and must both read. Eleven cells of separation:
    // outside the marksman rifle's 9.5, so the two rows pose instead of
    // opening fire.
    sim.spawnDef(ID.Vector, 1, 60, 70)
    const vGun = sim.spawnDef(ID.Vector, 1, 63, 70)
    build.tryLoad(1, sim.spawnDef(ID.DroneSwarm, 1, 63, 71), vGun)
    const vLong = sim.spawnDef(ID.Vector, 1, 66, 70)
    build.tryLoad(1, sim.spawnDef(ID.Nullifier, 1, 66, 71), vLong)
    const vCrane = sim.spawnDef(ID.Vector, 1, 69, 70)
    build.tryLoad(1, sim.spawnDef(ID.Infiltrator, 1, 69, 71), vCrane)
    reveal(sim)
  },

  'roster-helios': (sim) => {
    grid(sim, HELIOS_ROSTER, 0, 24, 24)
    reveal(sim)
  },

  'roster-ascendancy': (sim) => {
    grid(sim, ASCENDANCY_ROSTER, 1, 24, 24)
    reveal(sim)
  },

  stress: (sim) => {
    // The 400-unit frame the performance gate is written against.
    const helios = defsForFaction(FACTION.Helios).filter((d) => !d.isStructure)
    const ascendancy = defsForFaction(FACTION.Ascendancy).filter((d) => !d.isStructure)
    for (let i = 0; i < 200; i++) {
      sim.spawnDef(helios[i % helios.length].id, 0, 44 + (i % 20), 50 + ((i / 20) | 0))
      sim.spawnDef(ascendancy[i % ascendancy.length].id, 1, 68 + (i % 20), 50 + ((i / 20) | 0))
    }
    reveal(sim)
  },
}

/** Reveal the map for slot 0, so a shot is not mostly fog. */
function reveal(sim: Sim): void {
  const vision = sim.registry.get<Vision>('vision')
  vision.revealAll(0)
}

export function populatorFor(name: string | undefined): Populator | undefined {
  return name ? POPULATORS[name] : undefined
}
